import {Box, Button, Typography} from '@mui/material';
import './ProcessingSyncSummaryPanel.scss';
import {t} from 'i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {useProcSyncSummary} from './hooks/useProcSync';

export const TEST_IDS = {
  CONTAINER: 'processing-sync-summary-panel',
  REFRESH_BUTTON: 'summary-refresh-button',
  CONSUMER_LAST_PROCESS: 'summary-consumer-last-process',
  WORKSPACE_LAST_PROCESS: 'summary-workspace-last-process',
  SYNC_HISTORY: 'summary-sync-history',
  SYNC_ERRORS: 'summary-sync-errors',
} as const;

interface ProcessingSyncSummaryPanelProps {
  onRefresh?: () => void;
}

const ProcessingSyncSummaryPanel = ({
  onRefresh,
}: ProcessingSyncSummaryPanelProps) => {
  const {data, isLoading, isError, refetch} = useProcSyncSummary();

  const shouldShowLoader = isLoading || !data;

  const formatDate = (input: string): string => {
    const date = new Date(input);

    if (Number.isNaN(date.getTime())) return 'Invalid Date';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };
  const handleRefresh = () => {
    refetch();
    onRefresh?.();
  };

  return (
    <Box
      className="processing-sync-summary-panel"
      data-testid={TEST_IDS.CONTAINER}
    >
      <Box className="panel-header-row">
        <Typography
          className="typo-text subhead2_size panel-section-title"
          data-testid="summary-section-heading"
        >
          {t('ingestions.mapped.processingsync.summary')}
        </Typography>
        <Button
          className="refresh-button"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          data-testid={TEST_IDS.REFRESH_BUTTON}
        >
          {t('ingestions.mapped.processingsync.refresh')}
        </Button>
      </Box>

      {isError && (
        <Box
          className="summary-panel-error"
          role="alert"
          data-testid="summary-panel-error"
        >
          <ErrorOutlineIcon data-testid="summary-panel-error-icon" />
          <Typography
            className="typo-text body3_size"
            data-testid="summary-panel-error-message"
          >
            {t('ingestions.mapped.processingsync.loadError')}
          </Typography>
        </Box>
      )}

      <Box className="summary-content">
        <Typography
          className="typo-text body2_size summary-row"
          data-testid={TEST_IDS.CONSUMER_LAST_PROCESS}
        >
          <span className="summary-label">
            {t('ingestions.mapped.processingsync.consumerLastProcess')}
          </span>{' '}
          {shouldShowLoader ? (
            <ArrLoadingSpinner size={14} aria-label={t('common.dataLoading')} />
          ) : (
            formatDate(data.consumerLastProcess)
          )}
        </Typography>

        <Typography
          className="typo-text body2_size summary-row"
          data-testid={TEST_IDS.WORKSPACE_LAST_PROCESS}
        >
          <span className="summary-label">
            {t('ingestions.mapped.processingsync.workspaceLastProcess')}
          </span>{' '}
          {shouldShowLoader ? (
            <ArrLoadingSpinner size={14} aria-label={t('common.dataLoading')} />
          ) : (
            formatDate(data.workspaceLastProcess)
          )}
        </Typography>

        <Typography
          className="typo-text body2_size summary-row"
          data-testid={TEST_IDS.SYNC_HISTORY}
        >
          <span className="summary-label">
            {t('ingestions.mapped.processingsync.syncHistory')}
          </span>{' '}
          {shouldShowLoader ? (
            <ArrLoadingSpinner size={14} aria-label={t('common.dataLoading')} />
          ) : (
            data.syncHistoryCount
          )}
        </Typography>

        <Typography
          className="typo-text body2_size summary-row"
          data-testid={TEST_IDS.SYNC_ERRORS}
        >
          <span className="summary-label">
            {t('ingestions.mapped.processingsync.syncErrors')}
          </span>{' '}
          {shouldShowLoader ? (
            <ArrLoadingSpinner size={14} aria-label={t('common.dataLoading')} />
          ) : (
            data.syncErrorCount
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProcessingSyncSummaryPanel;
